import {
    buildExportFilename,
    downloadItemsAsCSV,
    downloadTimeSeriesAsCSV,
    getHierarchyCSVColumns,
} from 'utils/formatCSV';
import bugsnagClient from 'containers/BugsnagClient';
import { downloadTypes } from 'components/Button/DownloadCSVMenuButton';
import getAllTimeSeriesData from 'utils/api/tiktokSearchTimeSeries';
import { getTikTokData } from 'utils/api/tiktok.api';
import { parseTikTokTimeSeries } from 'utils/helpers';
import toast from 'react-hot-toast';

const typeFromPaginateBy = (paginateBy) =>
    paginateBy === 'brands'
        ? 'brand'
        : paginateBy === 'hashtags'
          ? 'hashtag'
          : 'trend';

export function getTikTokDownloadOptions({
    entity,
    country,
    date,
    sortedData,
    visibleOrderedMetrics,
    paginateBy,
    brand,
    category,
    hashtag,
    isAdmin,
    shapeTimeSeries = parseTikTokTimeSeries,
    getTimeSeries = getTikTokData,
}) {
    const baseParams = {
        entity,
        country,
        source: 'tiktok',
        type: typeFromPaginateBy(paginateBy),
        date,
    };
    const countryIds = country?.id ? [country.id] : null;
    return [
        {
            mixpanelMeta: { label: 'table data csv' },
            children: 'Table Data CSV',
            onClick: () =>
                downloadItemsAsCSV(
                    buildExportFilename({ ...baseParams, kind: 'data' }),
                    sortedData,
                    [
                        { key: 'name' },
                        ...visibleOrderedMetrics.map(
                            ({ key, tableHeadColumnLabel: label }) => ({
                                key,
                                label,
                            }),
                        ),
                        {
                            key: 'cluster',
                            label: 'Cluster',
                            accessor: ({ cluster }) =>
                                cluster
                                    ? cluster.replace('tiktok ', '')
                                    : cluster,
                        },
                        { key: 'healthStats' },
                        ...(!!hashtag || brand
                            ? []
                            : getHierarchyCSVColumns(isAdmin)),
                    ],
                ),
            downloadType: downloadTypes.CSV,
            key: 'table data csv',
        },
        {
            mixpanelMeta: { label: 'time series csv' },
            children: 'Time Series CSV',
            onClick: () => {
                toast.promise(
                    (async () => {
                        try {
                            const categoryIds =
                                paginateBy === 'categories'
                                    ? Array.from(
                                          new Set(
                                              sortedData.map(
                                                  (dp) => dp.category,
                                              ),
                                          ),
                                      )
                                    : category;
                            const tsData = await getAllTimeSeriesData({
                                sortedData,
                                paginateBy,
                                country: countryIds,
                                brand,
                                category: categoryIds,
                                hashtag,
                                shapeTimeSeries,
                                getTimeSeries,
                            });
                            downloadTimeSeriesAsCSV(
                                buildExportFilename({
                                    ...baseParams,
                                    kind: 'time-series',
                                }),
                                tsData,
                            );
                        } catch (err) {
                            bugsnagClient.notify(err);
                            throw err;
                        }
                    })(),
                    {
                        loading: 'Preparing download...',
                        success: 'Download complete!',
                        error: 'Error downloading time series CSV',
                    },
                );
            },
            downloadType: downloadTypes.CSV,
            key: 'time series csv',
        },
    ];
}

export function getTikTokDownloadOptionsAsync({
    entity,
    country,
    date,
    visibleOrderedMetrics,
    paginateBy,
    getQueryData,
    shapeTimeSeries = parseTikTokTimeSeries,
    getTimeSeries = getTikTokData,
}) {
    const baseParams = {
        entity,
        country,
        source: 'tiktok',
        type: typeFromPaginateBy(paginateBy),
        date,
    };
    const countryIds = country?.id ? [country.id] : null;
    return [
        {
            mixpanelMeta: { label: 'table data csv' },
            children: 'Table Data CSV',
            onClick: () => {
                toast.promise(
                    (async () => {
                        try {
                            const allData = await getQueryData();
                            downloadItemsAsCSV(
                                buildExportFilename({
                                    ...baseParams,
                                    kind: 'data',
                                }),
                                allData,
                                [
                                    { key: 'name' },
                                    ...visibleOrderedMetrics.map(
                                        ({
                                            key,
                                            tableHeadColumnLabel: label,
                                        }) => ({ key, label }),
                                    ),
                                    {
                                        key: 'cluster',
                                        label: 'Cluster',
                                        accessor: ({ cluster }) =>
                                            cluster
                                                ? cluster.replace('tiktok ', '')
                                                : cluster,
                                    },
                                    { key: 'healthStats' },
                                ],
                            );
                        } catch (error) {
                            console.error('Error downloading CSV:', error);
                            throw error;
                        }
                    })(),
                    {
                        loading: 'Preparing download...',
                        success: 'Download complete!',
                        error: 'Error downloading CSV',
                    },
                );
            },
            downloadType: downloadTypes.CSV,
            key: 'table data csv',
        },
        {
            mixpanelMeta: { label: 'time series csv' },
            children: 'Time Series CSV',
            onClick: () => {
                toast.promise(
                    (async () => {
                        try {
                            const sortedData = await getQueryData();
                            const brand = new Set();
                            const category = new Set();
                            const hashtag = new Set();
                            sortedData.forEach((dataPoint) => {
                                if (dataPoint.brand) {
                                    brand.add(dataPoint.brand);
                                }
                                if (dataPoint.category) {
                                    category.add(dataPoint.category);
                                }
                                if (dataPoint.hashtag) {
                                    hashtag.add(dataPoint.hashtag);
                                }
                            });
                            const tsData = await getAllTimeSeriesData({
                                sortedData,
                                paginateBy,
                                country: countryIds,
                                hashtag: hashtag.size
                                    ? Array.from(hashtag)
                                    : null,
                                shapeTimeSeries,
                                getTimeSeries,
                            });
                            downloadTimeSeriesAsCSV(
                                buildExportFilename({
                                    ...baseParams,
                                    kind: 'time-series',
                                }),
                                tsData,
                            );
                        } catch (err) {
                            bugsnagClient.notify(err);
                            throw err;
                        }
                    })(),
                    {
                        loading: 'Preparing download...',
                        success: 'Download complete!',
                        error: 'Error downloading time series CSV',
                    },
                );
            },
            downloadType: downloadTypes.CSV,
            key: 'time series csv',
        },
    ];
}
